import { useEffect, useState } from 'react'
import cx from 'classnames'
import { useStores } from 'hooks/useStores'
import debounce from 'lodash/debounce'
import set from 'lodash/set'
import trim from 'lodash/trim'
import { observer } from 'mobx-react'
import { deleteGroupById } from 'API/groups'
import Button from 'components/Button'
import DeleteDialog from 'components/DeleteDialog'
import ModalDialog, { ModalDialogProps } from 'components/ModalDialog'
import TextField from 'components/TextField'
import { IGroupMember, IGroupMemberPermissionId } from 'interfaces/group'
import { getValidArray } from 'utils/common'
import { ITheme } from '../../interfaces/theme'
import { primary } from '../../theme/globalStyles'
import GroupOption from './components/GroupOption'
import styles from './styles.module.scss'

interface IControlledShareWithGroupsModalProps extends Omit<ModalDialogProps, 'headless'> {
  onSubmit: () => void
  setSelectedGroupIds: (groupIds: number[]) => void
  selectedGroupIds: number[]
  isBasicUser: boolean
}

const ControlledShareWithGroupsModal = ({
  className,
  onSubmit,
  setSelectedGroupIds,
  selectedGroupIds,
  isBasicUser,
  ...props
}: IControlledShareWithGroupsModalProps) => {
  const { groupStore, organizationStore, authStore } = useStores()
  const { organization } = organizationStore
  const { groups, groupMembers } = groupStore
  const currentTheme: ITheme = organization?.theme ?? {}

  const [searchText, changeSearchText] = useState('')
  const [deleteGroupId, setDeleteGroupId] = useState(0)
  // organizationStore.organization is only hydrated on subdomain/appearance flows;
  // fall back to the active org so group loading works everywhere (e.g. Search page).
  const organizationId = organization?.id ?? authStore.resolvedActiveOrgId ?? 0
  const filterGroup = {
    fields: ['name', 'id'],
    where: { organizationId, name: { like: `.*${trim(searchText)}.*`, options: 'i' } }
  }

  useEffect(() => {
    // API now handles permission filtering automatically
    groupStore.getGroups(filterGroup)
  }, [searchText])

  function toggleGroup(id: number): void {
    if (selectedGroupIds.includes(id)) {
      const newGroupIds = selectedGroupIds.filter(selectedId => selectedId !== id)
      setSelectedGroupIds(newGroupIds)
    } else {
      setSelectedGroupIds([...selectedGroupIds, id])
    }
  }

  function handleCancel(): void {
    setSelectedGroupIds([])
    props.toggle()
  }

  async function handleDeleteGroupConfirmed(): Promise<void> {
    if (deleteGroupId) {
      setDeleteGroupId(0)
      await deleteGroupById(deleteGroupId)
      groupStore.getGroups(filterGroup)
    }
  }

  const debounceSearchGroup = debounce(changeSearchText, 500)

  return (
    <ModalDialog headless className={cx(styles.shareGroupDialog, className)} {...props}>
      <div className="d-flex justify-content-between">
        <h4>Share with groups</h4>
      </div>
      <div className={styles.fieldWrapper}>
        <TextField
          placeholder="Search groups"
          onChange={(event: { currentTarget: { value: string } }) => {
            debounceSearchGroup(event.currentTarget.value)
          }}
        />
      </div>
      <div className={styles.groupList}>
        {Array.isArray(groups) &&
          groups.map(group => {
            const isSelected = selectedGroupIds.includes(Number(group?.id))
            return (
              <GroupOption
                key={group?.id}
                isSelected={isSelected}
                group={group}
                toggleGroup={toggleGroup}
                setDeleteGroupId={setDeleteGroupId}
              />
            )
          })}
      </div>
      <div className={styles.actionBtns}>
        <Button color="light" onClick={handleCancel}>
          Cancel
        </Button>
        <Button
          color="info"
          onClick={onSubmit}
          backgroundColor={currentTheme?.primaryColor ?? primary}
          _hover={{ opacity: 0.8 }}
          _focus={{ opacity: 1 }}
          _active={{ opacity: 1 }}
        >
          Save
        </Button>
      </div>
      <DeleteDialog
        isOpen={deleteGroupId > 0}
        message="Are you sure you want to DELETE this group?"
        toggle={() => setDeleteGroupId(0)}
        onDelete={handleDeleteGroupConfirmed}
        onCancel={() => setDeleteGroupId(0)}
      />
    </ModalDialog>
  )
}

export default observer(ControlledShareWithGroupsModal)
